import type { PlateMeta } from '../worker/protocol';
import type { Viewport } from './viewport';
import { coverageLabel } from './sep-ui';

/** A crop of the 1:1 film, in film pixels. */
export interface FilmRect { x: number; y: number; w: number; h: number }

/**
 * The viewer's `Viewport` as `filmWindow` reads it: `x`/`y` are the canvas-space
 * translation of the film's origin (`Viewport.tx`/`ty`) and `width`/`height` the
 * canvas size, so the film-space centre is `((width / 2 − x) / scale, …)`.
 */
export interface FilmViewport { x: number; y: number; scale: number; width: number; height: number }

/**
 * The 1:1 crop of the film to request for the current viewport: at most `max` px
 * on a side (a whole 12 000 px film would be hundreds of megabytes as an
 * ImageBitmap), centred on whatever the viewport has in the middle of the canvas,
 * and clamped so the window never runs off the film.
 *
 * A non-positive or non-finite scale (a viewport that has not been fitted yet)
 * has no meaningful centre, so the window falls back to the middle of the film
 * rather than producing NaN coordinates the worker would clamp to (0, 0).
 */
export function filmWindow(viewport: FilmViewport, filmW: number, filmH: number, max = 2048): FilmRect {
  const w = Math.max(1, Math.min(max, filmW));
  const h = Math.max(1, Math.min(max, filmH));
  const usable = Number.isFinite(viewport.scale) && viewport.scale > 0;
  const cx = usable ? (viewport.width / 2 - viewport.x) / viewport.scale : filmW / 2;
  const cy = usable ? (viewport.height / 2 - viewport.y) / viewport.scale : filmH / 2;
  const clamp = (v: number, hi: number) => Math.max(0, Math.min(Math.round(v), hi));
  return { x: clamp(cx - w / 2, filmW - w), y: clamp(cy - h / 2, filmH - h), w, h };
}

/**
 * True when the window the viewport now wants has drifted far enough from the
 * crop already on screen — more than `tolerance` of the current crop's own
 * width/height, in any of x, y, w or h — to be worth re-rendering the film for.
 * Below that the cached crop still covers what is visible, and re-requesting it
 * on every pan would rasterise a multi-megapixel film for a few pixels of shift.
 */
export function filmWindowMoved(current: FilmRect, next: FilmRect, tolerance = 0.25): boolean {
  return Math.abs(next.x - current.x) > tolerance * current.w
    || Math.abs(next.y - current.y) > tolerance * current.h
    || Math.abs(next.w - current.w) > tolerance * current.w
    || Math.abs(next.h - current.h) > tolerance * current.h;
}

/**
 * The viewport the film view opens at: 100 %, film centred in the canvas —
 * deliberately NOT `fitViewport`. The view is labelled "Film 1:1" and is there
 * to judge dot shape and edge quality, which only reads true at one film pixel
 * per image pixel; fitting a 13×19 in film into the canvas would show it at
 * about 5 %, where every halftone dot is an aliasing artefact. At 100 % the
 * 2048 px window the worker hands over also covers the whole visible canvas, so
 * the first frame is fully inked rather than mostly bare paper.
 */
export function filmViewport(filmW: number, filmH: number, canvasW: number, canvasH: number): Viewport {
  return { scale: 1, tx: (canvasW - filmW) / 2, ty: (canvasH - filmH) / 2 };
}

/** The plate select's options: the composite proof first, then every plate in
 *  press order with its coverage. */
export function plateOptions(plates: PlateMeta[]): Array<{ value: string; label: string }> {
  return [
    { value: 'proof', label: 'Composite proof' },
    ...plates.map((p) => ({ value: p.id, label: `${p.name} · ${coverageLabel(p)}` })),
  ];
}
